/**
 * Terms of Use and Privacy Policy, transcribed verbatim from:
 *   StudioIP_Terms_of_Use_Proposed_Final_Both_Domains.docx
 *   StudioIP_Privacy_Policy_Proposed_Final_Both_Domains.docx
 *
 * This file is a byte-for-byte copy of the studioip-website repo's
 * src/lib/legal.ts, which renders the public /terms-of-use and
 * /privacy-policy pages. Keep the two identical: the registration modals
 * here and the public pages must show the same text.
 *
 * These are INTERIM versions pending legal approval. When the approved
 * documents arrive, replace the text below AND bump `version` in BOTH repos,
 * and add the new version to the backends' accepted-version lists
 * (STUDIOIP_*_VERSIONS). The backends store the version with each user's
 * consent, so a text change without a version bump makes old consent records
 * ambiguous.
 */

export interface LegalSection {
  /** Heading exactly as numbered in the source document. */
  heading: string;
  paragraphs: string[];
}

export interface LegalDocument {
  title: string;
  version: string;
  scopeLine: string;
  sections: LegalSection[];
}

export const TERMS_OF_USE: LegalDocument = {
  title: 'StudioIP | Terms of Use',
  version: 'interim-2026-11-02',
  scopeLine:
    'Websites: www.studioip.in and www.studioip.ai  •  Effective upon publication after legal approval',
  sections: [
    {
      heading: '1. Scope and parties',
      paragraphs: [
        "These Terms govern use of StudioIP, a business-facing creative project planning, vendor discovery, structured bidding and project coordination platform operated by Mahatta Multimedia Pvt. Ltd. ('StudioIP', 'Company', 'we'). StudioIP is a distinct service from IndiaPicture. These Terms do not govern IndiaPicture products or services.",
        "A 'Client' is an organization or its authorized representative seeking creative services. A 'Vendor' is an independent creative studio or service provider offering such services. 'User' includes both. Accessing or registering for StudioIP constitutes acceptance of these Terms. An individual accepting for an organization confirms authority to bind it.",
      ],
    },
    {
      heading: '2. Platform role',
      paragraphs: [
        'StudioIP supports project planning, suitable vendor discovery, invitations to bid, communication, workflow monitoring and recording of approvals. It is not a freelance job board or an open, unrestricted bidding marketplace. Vendors remain independent businesses, not employees or agents of the Company solely because they use the platform.',
        "Unless the applicable signed project agreement expressly states otherwise, StudioIP does not itself undertake creative production or warrant a Vendor's deliverables. StudioIP may facilitate oversight and escalation without assuming the Vendor's contractual production obligations.",
      ],
    },
    {
      heading: '3. Accounts and eligibility',
      paragraphs: [
        'Users must be at least 18 years old, legally capable of contracting and authorized to act for their organizations. Users must provide accurate registration and profile information, protect credentials, manage access permissions and promptly report unauthorized account activity.',
        'StudioIP may evaluate and verify Vendor profiles, portfolios and capabilities. Verification means a platform review under its current processes; it is not a guarantee of skill, legal compliance, performance or future availability. Registration, verification or listing does not guarantee invitations, bids, project awards or revenue.',
      ],
    },
    {
      heading: '4. Planning and AI assistance',
      paragraphs: [
        'StudioIP may provide AI-assisted planning tools, including Nur, to help structure project briefs and identify information needed for a project. AI-generated suggestions may be inaccurate or incomplete. Clients remain responsible for reviewing and confirming the scope, specifications, budgets and timelines they approve.',
        'AI outputs are decision support, not binding professional advice, final procurement decisions or a promise of vendor performance. Users must not submit third-party confidential information or personal data unless they are authorized to do so. Processing of information is described in the Privacy Policy.',
      ],
    },
    {
      heading: '5. Briefs, proposals and bidding',
      paragraphs: [
        'Clients are responsible for the accuracy and lawful use of briefs, assets and instructions. Vendors are responsible for the truthfulness of profiles, proposals, qualifications, availability and bids. StudioIP may recommend or invite Vendors based on category, capabilities, experience and other project-fit considerations.',
        'An invitation to bid does not guarantee selection. A submitted proposal is subject to its stated validity and the applicable contractual process. No specific number of bids, price, award or turnaround is promised. Confidential briefs and bid information may be accessed only by authorized project participants.',
      ],
    },
    {
      heading: '6. Project terms and order of precedence',
      paragraphs: [
        "Individual projects may be governed by a separately accepted or signed Terms of Engagement, statement of work, project agreement or written amendment (together, 'Project Agreement'). The Project Agreement determines deliverables, milestones, commercial terms, taxes, acceptance criteria, cancellation rights, ownership of work product, payments and remedies.",
        'If these Terms conflict with an applicable signed Project Agreement on project-specific matters, that Project Agreement prevails for that project. No website description or marketing message changes an executed Project Agreement.',
      ],
    },
    {
      heading: '7. Approvals, communication and changes',
      paragraphs: [
        'The project workflow may require version-specific approvals for scripts, storyboards, creative concepts, milestones or deliverables. Where StudioIP provides a designated approval process, approvals must be recorded in that process or another method expressly recognized by the Project Agreement.',
        'Informal discussions, including email or messaging-app exchanges, do not by themselves amend a signed Project Agreement. Material scope, price or schedule changes require documented review and approval in accordance with the Project Agreement. StudioIP may retain project records and communication history to administer projects and resolve disputes, as explained in the Privacy Policy.',
      ],
    },
    {
      heading: '8. Commercial terms',
      paragraphs: [
        'Fees, any platform charges, Vendor remuneration, invoicing, payment timing, tax treatment, refunds, cancellation charges and payment disputes are governed by the applicable Project Agreement and any commercial terms expressly accepted at the point of transaction. StudioIP will not impose undisclosed charges.',
        'Nothing in these Terms guarantees collection, payment, refund or completion where those obligations are not expressly undertaken in a Project Agreement.',
      ],
    },
    {
      heading: '9. Intellectual property and confidentiality',
      paragraphs: [
        'Users retain rights in their pre-existing content and warrant that they have the permissions required to submit materials. Users grant StudioIP a limited, non-exclusive permission to store, process, transmit and display uploaded materials to authorized participants as necessary to operate the service and administer projects.',
        "Ownership and licensing of commissioned deliverables, source files, third-party materials and unused concepts are governed by the Project Agreement. Users must protect non-public briefs, proposals, pricing, personal data and project materials and may disclose them only as authorized or legally required. The Company's software, brand and platform content remain its or its licensors' property.",
      ],
    },
    {
      heading: '10. Acceptable use',
      paragraphs: [
        "Users must not submit unlawful, misleading, infringing or malicious content; impersonate others; access another user's confidential information without permission; manipulate bidding; circumvent security; scrape the service without authorization; or use StudioIP in a manner that disrupts others.",
        'We may remove content or restrict access where reasonably necessary to address a breach, protect users, comply with law or maintain platform security. Where appropriate, we may provide notice and an opportunity to respond.',
      ],
    },
    {
      heading: '11. Service and liability',
      paragraphs: [
        'We use reasonable efforts to operate StudioIP, but availability, error-free operation, AI accuracy, vendor suitability, bid response and creative outcomes cannot be guaranteed. Nothing in these Terms excludes rights or liabilities that cannot lawfully be excluded.',
        'To the extent permitted by applicable law, the Company is not liable for indirect or consequential loss arising solely from use of the platform. Liability arising from a specific project, including any agreed caps or exceptions, is determined by the relevant Project Agreement and applicable law.',
      ],
    },
    {
      heading: '12. Suspension and closure',
      paragraphs: [
        'Users may request closure of their accounts by contacting info@studioip.in. We may suspend or close accounts for material breach, misuse, security risk or legal necessity. Account closure does not automatically terminate active Project Agreements, extinguish payment obligations or require deletion of records that must be retained lawfully.',
      ],
    },
    {
      heading: '13. Law, disputes and contact',
      paragraphs: [
        'These Terms are governed by the laws of India. The parties will first attempt to resolve platform-related disputes through good-faith discussions. Subject to any binding dispute-resolution provision in an applicable Project Agreement and mandatory law, courts of competent jurisdiction in New Delhi will have jurisdiction.',
        'For support, legal notices or privacy queries, contact info@studioip.in. Registered office: 8/17, Sarvapriya Vihar, New Delhi – 110016. We may revise these Terms and will notify users of material changes where required by law.',
        'Domain coverage: These Terms apply to StudioIP services accessed through both www.studioip.in and www.studioip.ai, including associated account, planning, bidding and project-management functions. References to “StudioIP” or the “Platform” cover both domains.',
      ],
    },
  ],
};

export const PRIVACY_POLICY: LegalDocument = {
  title: 'StudioIP | Privacy Policy',
  version: 'interim-2026-11-02',
  scopeLine:
    'Websites: www.studioip.in and www.studioip.ai  •  Effective upon publication after legal approval',
  sections: [
    {
      heading: '1. Controller and scope',
      paragraphs: [
        "Mahatta Multimedia Pvt. Ltd. ('we', 'Company') operates StudioIP through www.studioip.in and www.studioip.ai (together, the “StudioIP Websites”). This Policy applies to both domains and their associated StudioIP services. This Policy explains how personal information is handled in connection with StudioIP's client and vendor accounts, project planning, structured bidding, project communications and execution workflows. StudioIP is a separate service from IndiaPicture; this Policy does not automatically apply to IndiaPicture's business or websites.",
      ],
    },
    {
      heading: '2. Information we collect',
      paragraphs: [
        'We collect information supplied by users, which may include names, work contact details, organization and role, account credentials, Vendor profiles and portfolios, qualifications, project briefs, proposals, attachments, messages, approvals, milestones, billing details and support requests.',
        'We may also collect service usage information, access logs, IP addresses, browser/device details and cookies needed for security, operation, performance analysis or other disclosed purposes. Project uploads may contain confidential business information or personal data about individuals identified in the materials.',
      ],
    },
    {
      heading: '3. Purposes of processing',
      paragraphs: [
        'We use information to register and authenticate accounts; evaluate Vendor profiles; assist with project planning; identify relevant Vendors; invite and assess bids; administer projects, approvals and changes; support billing and service communications; prevent misuse; address disputes; comply with law; and improve the service.',
        'Where permitted by applicable law and relevant communication preferences, we may send StudioIP product updates, relevant project or Vendor information and marketing messages. Users may opt out of non-essential marketing using the unsubscribe option provided or by contacting info@studioip.in.',
      ],
    },
    {
      heading: '4. AI-assisted functions',
      paragraphs: [
        'Where AI-assisted features are used, submitted prompts, project descriptions and relevant documents may be processed by technology providers supporting the service to generate planning suggestions or other requested outputs. AI-generated results are reviewed by users before project decisions are finalized.',
        'We seek to limit provider access and use to what is reasonably needed to deliver the relevant feature, subject to our contractual arrangements and applicable law. Users should not enter sensitive personal data or confidential third-party material without appropriate authorization. Information about specific providers and any applicable notices will be provided where required by law.',
      ],
    },
    {
      heading: '5. Sharing and visibility',
      paragraphs: [
        'Relevant project information may be made available to authorized Client and Vendor participants for discovery, invitations, bidding, project coordination and delivery. Different users may see different fields depending on their role and project permissions.',
        'We may engage hosting, communication, analytics, payment, security and AI technology providers to process information on our behalf; and disclose information where necessary for legal compliance, protection of rights, fraud prevention or dispute resolution. We do not make private briefs or proposals publicly available merely because they are uploaded to StudioIP.',
        'Information collected through IndiaPicture is not automatically treated as StudioIP account information. Any sharing between the two services must have an appropriate legal basis, notice and controls.',
      ],
    },
    {
      heading: '6. Confidentiality and security',
      paragraphs: [
        'We apply reasonable technical and organizational safeguards designed to protect information from unauthorized access, loss, misuse or disclosure. Access is intended to be restricted to personnel, service providers and project participants with a legitimate need. No internet-based system can be guaranteed completely secure.',
      ],
    },
    {
      heading: '7. Retention',
      paragraphs: [
        'We keep personal information for as long as reasonably needed for account administration, active projects, contracts, legal and tax obligations, security, dispute resolution and other lawful purposes. Retention may continue after account closure where required or permitted by law. When information is no longer needed, it is deleted or de-identified in accordance with applicable processes.',
      ],
    },
    {
      heading: '8. Rights and preferences',
      paragraphs: [
        'Subject to applicable law, individuals may request information about their personal data, correction or updating, withdrawal of consent where relevant, erasure where available, and grievance redressal. Requests may be made at info@studioip.in. Certain records may need to be retained despite a deletion request to satisfy contractual or legal obligations.',
        'Essential project and account notifications may continue while needed to provide contracted services. Non-essential promotional communications can be opted out of separately.',
      ],
    },
    {
      heading: '9. Cookies, external services and transfers',
      paragraphs: [
        'StudioIP may use cookies or similar technologies for login, security, functionality and service measurement. Users can manage cookies through available browser settings, although disabling essential cookies may affect functionality.',
        "Service providers may process information from locations outside the user's jurisdiction where permitted by applicable law and subject to appropriate safeguards. Third-party websites linked from StudioIP operate under their own privacy policies.",
      ],
    },
    {
      heading: '10. Changes and contact',
      paragraphs: [
        'We may update this Policy and will publish the revised version on StudioIP, with notice where legally required. Questions, requests or complaints may be sent to info@studioip.in.',
        'Mahatta Multimedia Pvt. Ltd., 8/17, Sarvapriya Vihar, New Delhi – 110016.',
      ],
    },
  ],
};

export type LegalDocKey = 'terms' | 'privacy';

export const LEGAL_DOCUMENTS: Record<LegalDocKey, LegalDocument> = {
  terms: TERMS_OF_USE,
  privacy: PRIVACY_POLICY,
};

/** Public pages on the marketing site (no login required). */
export const LEGAL_PAGE_URLS: Record<LegalDocKey, string> = {
  terms: 'https://studioip.ai/terms-of-use',
  privacy: 'https://studioip.ai/privacy-policy',
};

/**
 * Fields sent with a registration request once the user has ticked the
 * consent checkbox. The backend stamps the acceptance time, IP and user agent
 * itself and only accepts versions it knows, so nothing here is trusted
 * beyond "this user ticked the box for these document versions".
 */
export interface LegalConsentPayload {
  terms_accepted: true;
  terms_version: string;
  privacy_version: string;
}

export function legalConsentPayload(): LegalConsentPayload {
  return {
    terms_accepted: true,
    terms_version: TERMS_OF_USE.version,
    privacy_version: PRIVACY_POLICY.version,
  };
}